import { DestroyRef, Directive, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { MutationObserverService } from '@frontend/sports/common/core/utils/dom';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { TimerService } from '@frontend/sports/common/core/utils/timer';
import { DeviceService } from '@frontend/vanilla/core';

/**
 * A directive that enables automatic scrolling of text for overflowing content, often referred to as a marquee effect.
 * This directive supports responsive interaction, including mouse hover, touch events, and automatic scroll initiation.
 *
 * Usage:
 * Add the `msTextAutoScroll` attribute to an element. Configure behavior using available input properties.
 *
 * Inputs:
 * - `marqueeAutoScroll`: Enables automatic scrolling after a delay (default: `false`).
 * - `ngDisabled`: Disables the marquee functionality (default: `false`).
 * - `ignoreMouseEvents`: Ignores mouse hover events (default: `false`).
 * - `ignoreTouchEvents`: Ignores touch events (default: `false`).
 * - `allowEventPropagation`: Allows events to propagate beyond the directive (default: `false`).
 */
@Directive({
    selector: '[msTextAutoScroll]',
    standalone: true,
})
export class TextAutoScrollDirective implements OnChanges, OnInit, OnDestroy {
    @Input() marqueeAutoScroll = false;
    @Input() ngDisabled = false;
    @Input() ignoreMouseEvents = false;
    @Input() ignoreTouchEvents = false;
    @Input() allowEventPropagation = false;

    private intervalUnsubscribe: (() => void) | null = null;
    private readonly WAIT_DELAY = 25;
    private readonly INTERVAL_DELAY = 30;
    private readonly START_INTERVAL_DELAY = 5000;
    private readonly marqueeWorkingClass = 'marquee-working';

    private readonly element: ElementRef<HTMLElement> = inject(ElementRef) as ElementRef<HTMLElement>;
    private readonly timerService: TimerService = inject(TimerService);
    private readonly mutationObserver: MutationObserverService = inject(MutationObserverService);
    private readonly deviceService: DeviceService = inject(DeviceService);
    private readonly destroyRef = inject(DestroyRef);

    ngOnInit(): void {
        this.initAutoScroll();
        this.initMutationObserver();
    }

    ngOnChanges(changes: ISimpleChanges<TextAutoScrollDirective>): void {
        if ('ngDisabled' in changes) {
            const value = changes.ngDisabled;
            if ((value && !this.intervalUnsubscribe) || (!value && !!this.intervalUnsubscribe)) {
                this.toggleMarquee();
            }
        }
    }

    ngOnDestroy(): void {
        this.stopInterval();
    }

    initAutoScroll = () => {
        if (this.marqueeAutoScroll) {
            this.timerService.setTimeout(() => {
                if (!this.intervalUnsubscribe) {
                    this.toggleMarquee();
                }
            }, this.START_INTERVAL_DELAY);
        }
    };

    stopInterval = () => {
        if (this.intervalUnsubscribe) {
            this.intervalUnsubscribe();
        }
        this.element.nativeElement.classList.remove(this.marqueeWorkingClass);
        this.intervalUnsubscribe = null;
    };

    @HostListener('mouseover', ['$event'])
    hoverHandler = (e: Event) => {
        if (!this.deviceService.isMobile && !this.ignoreMouseEvents) {
            if (!this.allowEventPropagation) {
                e.stopPropagation();
            }
            this.toggleMarquee();
        }
    };

    @HostListener('mouseout', ['$event'])
    mouseOutHandler = (e: Event) => {
        if (!this.deviceService.isMobile && !this.ignoreMouseEvents) {
            if (!this.allowEventPropagation) {
                e.stopPropagation();
            }
            this.stopInterval();
            this.element.nativeElement.style.textIndent = '0px';
        }
    };

    @HostListener('touchend', ['$event'])
    touchHandler = (e: Event) => {
        if (!this.ignoreTouchEvents) {
            if (!this.allowEventPropagation) {
                e.stopPropagation();
            }
            this.toggleMarquee();
        }
    };

    toggleMarquee = () => {
        this.element.nativeElement.style.textIndent = '0';

        if (this.intervalUnsubscribe) {
            this.stopInterval();
            return;
        }

        let counter = 0;
        let clientWidth = this.element.nativeElement.clientWidth;
        let scrollWidth = this.element.nativeElement.scrollWidth;
        // add 1px because clientWidth and scrollWidth return integers and not float
        // this way we always show the full text
        let pixelsToScroll = scrollWidth - clientWidth + 1;
        if (scrollWidth === clientWidth) {
            pixelsToScroll = 0;
        }

        this.element.nativeElement.classList.add(this.marqueeWorkingClass);

        const intervalCallback = () => {
            clientWidth = this.element.nativeElement.clientWidth;
            scrollWidth = this.element.nativeElement.scrollWidth;
            const crtIndent = parseFloat(this.element.nativeElement.style.textIndent);

            if (counter === 0 && pixelsToScroll) {
                this.element.nativeElement.style.textIndent = `${crtIndent - 1}px`;
                pixelsToScroll--;
            } else if (counter === this.WAIT_DELAY) {
                if (crtIndent >= 0) {
                    this.element.nativeElement.style.textIndent = '0px';
                    this.stopInterval();

                    return;
                } else {
                    this.element.nativeElement.style.textIndent = `${crtIndent + 1}px`;
                    counter++;
                }
            } else if (counter > this.WAIT_DELAY && clientWidth <= scrollWidth) {
                if (crtIndent !== 0) {
                    this.element.nativeElement.style.textIndent = `${crtIndent + 1}px`;
                } else {
                    this.stopInterval();

                    return;
                }
            } else {
                counter++;
            }
        };

        this.intervalUnsubscribe = this.timerService.setInterval(intervalCallback, this.INTERVAL_DELAY);
    };

    private initMutationObserver(): void {
        const target = this.element.nativeElement;
        const options = {
            characterData: true,
            characterDataOldValue: true,
            childList: true,
            subtree: true,
        };

        this.mutationObserver
            .observe(target, options)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.stopInterval();
                this.element.nativeElement.style.textIndent = '0px';
                this.initAutoScroll();
            });
    }
}
